import { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Building2, Send, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { NewsletterForm } from '@/components/NewsletterForm';
import { IMAGES } from '@/assets/images';
import { springPresets } from '@/lib/motion';

interface FormData {
  name: string;
  email: string;
  phone: string;
  company: string;
  subject: string;
  message: string;
  inquiryType?: string;
  productCategory?: string;
  adSize?: string;
}

export default function Contact() {
  const [activeTab, setActiveTab] = useState('general');
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: '',
    inquiryType: '',
    productCategory: '',
    adSize: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setIsSubmitting(false);
    setIsSubmitted(true);
    
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        subject: '',
        message: '',
        inquiryType: '',
        productCategory: '',
        adSize: '',
      });
    }, 3000);
  };

  const officeLocations = [
    {
      city: 'Singapore',
      address: '123 Aviation Boulevard, Changi Business Park',
      postal: 'Singapore 486789',
      phone: '+65 6789 1234',
      email: 'singapore@airportsecurity.asia',
    },
    {
      city: 'Hong Kong',
      address: '456 Airport Road, Kowloon Bay',
      postal: 'Hong Kong',
      phone: '+852 2345 6789',
      email: 'hongkong@airportsecurity.asia',
    },
    {
      city: 'Tokyo',
      address: '789 Haneda Terminal, Ota City',
      postal: 'Tokyo 144-0041, Japan',
      phone: '+81 3 1234 5678',
      email: 'tokyo@airportsecurity.asia',
    },
  ];

  return (
    <div className="min-h-screen">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={springPresets.gentle}
        className="relative bg-gradient-to-br from-primary/5 via-background to-accent/5 py-24 overflow-hidden"
      >
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.BUSINESS_MEETING_2}
            alt="Contact Us"
            className="w-full h-full object-cover opacity-10"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/60 to-background" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springPresets.gentle, delay: 0.1 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
              Get in Touch
            </h1>
            <p className="text-xl text-muted-foreground">
              Connect with us for supplier listings, advertising opportunities, or general inquiries about airport security solutions
            </p>
          </motion.div>
        </div>
      </motion.div>

      <div className="container mx-auto px-4 py-16">
        <div className="grid lg:grid-cols-3 gap-12">
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ ...springPresets.gentle, delay: 0.2 }}
            className="lg:col-span-2"
          >
            <Card className="border-2 shadow-lg">
              <CardHeader>
                <CardTitle className="text-3xl">Send Us a Message</CardTitle>
                <CardDescription className="text-base">
                  Choose the inquiry type that best matches your needs
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                  <TabsList className="grid w-full grid-cols-3 mb-8">
                    <TabsTrigger value="general">General Inquiry</TabsTrigger>
                    <TabsTrigger value="supplier">Supplier Listing</TabsTrigger>
                    <TabsTrigger value="advertising">Advertising</TabsTrigger>
                  </TabsList>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <TabsContent value="general" className="space-y-6 mt-0">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="name">Full Name *</Label>
                          <Input
                            id="name"
                            placeholder="John Doe"
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email">Email Address *</Label>
                          <Input
                            id="email"
                            type="email"
                            placeholder="john@company.com"
                            value={formData.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            required
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="phone">Phone Number</Label>
                          <Input
                            id="phone"
                            type="tel"
                            placeholder="+1 234 567 8900"
                            value={formData.phone}
                            onChange={(e) => handleInputChange('phone', e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="company">Company Name</Label>
                          <Input
                            id="company"
                            placeholder="Your Company"
                            value={formData.company}
                            onChange={(e) => handleInputChange('company', e.target.value)}
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="subject">Subject *</Label>
                        <Input
                          id="subject"
                          placeholder="How can we help you?"
                          value={formData.subject}
                          onChange={(e) => handleInputChange('subject', e.target.value)}
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="message">Message *</Label>
                        <Textarea
                          id="message"
                          placeholder="Tell us more about your inquiry..."
                          rows={6}
                          value={formData.message}
                          onChange={(e) => handleInputChange('message', e.target.value)}
                          required
                        />
                      </div>
                    </TabsContent>

                    <TabsContent value="supplier" className="space-y-6 mt-0">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="supplier-name">Full Name *</Label>
                          <Input
                            id="supplier-name"
                            placeholder="John Doe"
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="supplier-email">Email Address *</Label>
                          <Input
                            id="supplier-email"
                            type="email"
                            placeholder="john@company.com"
                            value={formData.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            required
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="supplier-phone">Phone Number *</Label>
                          <Input
                            id="supplier-phone"
                            type="tel"
                            placeholder="+1 234 567 8900"
                            value={formData.phone}
                            onChange={(e) => handleInputChange('phone', e.target.value)}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="supplier-company">Company Name *</Label>
                          <Input
                            id="supplier-company"
                            placeholder="Your Company"
                            value={formData.company}
                            onChange={(e) => handleInputChange('company', e.target.value)}
                            required
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="product-category">Product Category *</Label>
                        <Select
                          value={formData.productCategory}
                          onValueChange={(value) => handleInputChange('productCategory', value)}
                        >
                          <SelectTrigger id="product-category">
                            <SelectValue placeholder="Select a category" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="security-screening">Security Screening</SelectItem>
                            <SelectItem value="access-control">Access Control</SelectItem>
                            <SelectItem value="surveillance">Surveillance Systems</SelectItem>
                            <SelectItem value="cybersecurity">Cybersecurity</SelectItem>
                            <SelectItem value="terminal-equipment">Terminal Equipment</SelectItem>
                            <SelectItem value="emergency-response">Emergency Response</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="supplier-message">Tell us about your products and services *</Label>
                        <Textarea
                          id="supplier-message"
                          placeholder="Describe your company, products, and why you'd like to be listed on our platform..."
                          rows={6}
                          value={formData.message}
                          onChange={(e) => handleInputChange('message', e.target.value)}
                          required
                        />
                      </div>
                    </TabsContent>

                    <TabsContent value="advertising" className="space-y-6 mt-0">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="ad-name">Full Name *</Label>
                          <Input
                            id="ad-name"
                            placeholder="John Doe"
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="ad-email">Email Address *</Label>
                          <Input
                            id="ad-email"
                            type="email"
                            placeholder="john@company.com"
                            value={formData.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            required
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="ad-phone">Phone Number *</Label>
                          <Input
                            id="ad-phone"
                            type="tel"
                            placeholder="+1 234 567 8900"
                            value={formData.phone}
                            onChange={(e) => handleInputChange('phone', e.target.value)}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="ad-company">Company Name *</Label>
                          <Input
                            id="ad-company"
                            placeholder="Your Company"
                            value={formData.company}
                            onChange={(e) => handleInputChange('company', e.target.value)}
                            required
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="ad-size">Advertisement Size *</Label>
                        <Select
                          value={formData.adSize}
                          onValueChange={(value) => handleInputChange('adSize', value)}
                        >
                          <SelectTrigger id="ad-size">
                            <SelectValue placeholder="Select ad size" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="leaderboard">Leaderboard (728x90)</SelectItem>
                            <SelectItem value="rectangle">Rectangle (300x250)</SelectItem>
                            <SelectItem value="skyscraper">Skyscraper (160x600)</SelectItem>
                            <SelectItem value="full-page">Full Page Takeover</SelectItem>
                            <SelectItem value="newsletter">Newsletter Banner</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="ad-message">Campaign Details *</Label>
                        <Textarea
                          id="ad-message"
                          placeholder="Tell us about your advertising goals, target audience, and campaign duration..."
                          rows={6}
                          value={formData.message}
                          onChange={(e) => handleInputChange('message', e.target.value)}
                          required
                        />
                      </div>
                    </TabsContent>

                    <div className="flex items-center justify-between pt-4">
                      {isSubmitted ? (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          className="flex items-center gap-2 text-green-600"
                        >
                          <CheckCircle2 className="w-5 h-5" />
                          <span className="font-medium">Message sent successfully!</span>
                        </motion.div>
                      ) : (
                        <p className="text-sm text-muted-foreground">* Required fields</p>
                      )}
                      <Button
                        type="submit"
                        size="lg"
                        disabled={isSubmitting || isSubmitted}
                        className="min-w-[140px]"
                      >
                        {isSubmitting ? (
                          'Sending...'
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-2" />
                            Send Message
                          </>
                        )}
                      </Button>
                    </div>
                  </form>
                </Tabs>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ ...springPresets.gentle, delay: 0.3 }}
            className="space-y-6"
          >
            <Card className="border-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-primary" />
                  Company Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium">Email</p>
                    <a
                      href="mailto:info@airportsecurity.asia"
                      className="text-muted-foreground hover:text-primary transition-colors"
                    >
                      info@airportsecurity.asia
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium">Phone</p>
                    <a
                      href="tel:+6567891234"
                      className="text-muted-foreground hover:text-primary transition-colors"
                    >
                      +65 6789 1234
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium">Headquarters</p>
                    <p className="text-muted-foreground">
                      123 Aviation Boulevard<br />
                      Changi Business Park<br />
                      Singapore 486789
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2">
              <CardHeader>
                <CardTitle>Office Locations</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {officeLocations.map((office, index) => (
                  <motion.div
                    key={office.city}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ ...springPresets.gentle, delay: 0.4 + index * 0.1 }}
                    className="pb-6 border-b last:border-b-0 last:pb-0"
                  >
                    <h3 className="font-semibold text-lg mb-2">{office.city}</h3>
                    <p className="text-sm text-muted-foreground mb-1">{office.address}</p>
                    <p className="text-sm text-muted-foreground mb-2">{office.postal}</p>
                    <div className="space-y-1">
                      <a
                        href={`tel:${office.phone.replace(/\s/g, '')}`}
                        className="text-sm text-primary hover:underline block"
                      >
                        {office.phone}
                      </a>
                      <a
                        href={`mailto:${office.email}`}
                        className="text-sm text-primary hover:underline block"
                      >
                        {office.email}
                      </a>
                    </div>
                  </motion.div>
                ))}
              </CardContent>
            </Card>

            <Card className="border-2 bg-gradient-to-br from-primary/5 to-accent/5">
              <CardHeader>
                <CardTitle>Business Hours</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Monday - Friday</span>
                  <span className="font-medium">9:00 AM - 6:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Saturday</span>
                  <span className="font-medium">10:00 AM - 2:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Sunday</span>
                  <span className="font-medium">Closed</span>
                </div>
                <p className="text-xs text-muted-foreground pt-2">
                  All times are in Singapore Time (SGT, UTC+8)
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...springPresets.gentle, delay: 0.5 }}
          className="mt-16"
        >
          <Card className="border-2 bg-gradient-to-br from-primary/10 via-accent/5 to-primary/5">
            <CardContent className="py-12">
              <div className="max-w-2xl mx-auto text-center">
                <h2 className="text-3xl font-bold mb-4">Stay Updated</h2>
                <p className="text-muted-foreground mb-8">
                  Subscribe to our newsletter for the latest industry news, product updates, and exclusive insights
                </p>
                <NewsletterForm variant="default" />
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
